import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@chakra-ui/react';
import { getDaysLeft, getStatusPill, transformUserDetails } from '../../Library/helpers';
import BulkRecharge from './BulkRecharge';
import './style.css';
import PATHS from '../../Routes/paths';
import { SUBSCRIPTION_EXPIRED_DAYS_THRESHOLD, SUBSCRIPTION_SIM_BLOCK_THRESHOLD_DAYS_LEFT } from '../../Library/constants';
import { StoreContext } from '../../Library/contexts';

const PlansAndWarranty = () => {
  const navigate = useNavigate();
  const { setHasExpiredSubs, ensureSubsWarrantyData } = useContext(StoreContext);

  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedVehicleIds, setSelectedVehicleIds] = useState(new Set());
  const [isBulkModalOpen, setBulkModalOpen] = useState(false);

  const fetchData = (options = {}) => {
    const forceRefresh = !!options.forceRefresh;
    setLoading(true);
    setError(null);
    ensureSubsWarrantyData({ forceRefresh }, (err, data) => {
      if (err) {
        setError('Failed to load vehicle data');
        setLoading(false);
        return;
      }
      setApiData(data);
      const vehs = transformUserDetails(data);
      setHasExpiredSubs(vehs.some(v => {
        const days = getDaysLeft(v.subscriptionExpiry);
        return days <= SUBSCRIPTION_EXPIRED_DAYS_THRESHOLD && days > SUBSCRIPTION_SIM_BLOCK_THRESHOLD_DAYS_LEFT;
      }));
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const vehicles = useMemo(() => {
    if (!apiData) return [];
    return transformUserDetails(apiData);
  }, [apiData]);

  const filtered = useMemo(() => {
    let list = [...vehicles];

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(v =>
        v.displayName.toLowerCase().includes(q) ||
        v.regNumber.toLowerCase().includes(q)
      );
    }

    if (filter !== 'all') {
      list = list.filter(v => {
        const subDays = getDaysLeft(v.subscriptionExpiry);
        const warDays = getDaysLeft(v.warrantyExpiry);
        if (filter === 'expired') return (subDays < 0 && subDays > -999) || (warDays < 0 && warDays > -999);
        if (filter === 'expiring') return (subDays >= 0 && subDays <= 15) || (warDays >= 0 && warDays <= 15);
        if (filter === 'active') return subDays > 15 && warDays > 15;
        return true;
      });
    }

    list.sort((a, b) => {
      const aTime = a.subscriptionExpiry ? new Date(a.subscriptionExpiry).getTime() : 0;
      const bTime = b.subscriptionExpiry ? new Date(b.subscriptionExpiry).getTime() : 0;
      return aTime - bTime;
    });
    return list;
  }, [vehicles, search, filter]);

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedVehicleIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedVehiclesList = useMemo(
    () => vehicles.filter(v => selectedVehicleIds.has(v.id)),
    [vehicles, selectedVehicleIds]
  );

  if (isBulkModalOpen) {
    return (
      <BulkRecharge
        vehicles={selectedVehiclesList}
        apiData={apiData}
        onClose={() => { setBulkModalOpen(false); fetchData({ forceRefresh: true }); }}
      />
    );
  }

  const validSelectableIds = filtered.filter(v => getDaysLeft(v.subscriptionExpiry) > SUBSCRIPTION_SIM_BLOCK_THRESHOLD_DAYS_LEFT).map(v => v.id);
  const allSelected = validSelectableIds.length > 0 && selectedVehicleIds.size === validSelectableIds.length;

  return (
    <div className="panel-main pw-page">
      <div className="pw-header-sticky">
        <div className="pw-header-top">
          <div>
            <h1 className="pw-page-title">Plans & Warranty</h1>
            <p className="pw-tip">
              Tip: Select multiple vehicles using checkboxes to recharge them together.
            </p>
          </div>
          {selectedVehicleIds.size > 0 && (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                onClick={() => setSelectedVehicleIds(new Set())}
                style={{ fontSize: 13, background: 'none', border: 'none', color: 'var(--pw-text-secondary)', textDecoration: 'underline', fontWeight: 600, cursor: 'pointer' }}
              >
                Clear all
              </button>
              {selectedVehicleIds.size > 1 && (
                <button className="bulk-recharge-btn" onClick={() => setBulkModalOpen(true)}>
                  Recharge {selectedVehicleIds.size} Vehicles
                </button>
              )}
            </div>
          )}
        </div>

        <div className="filter-bar">
          <div className="filter-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input
              type="text"
              placeholder="Search vehicles..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="filter-pills">
            {['all', 'active', 'expiring', 'expired'].map(f => (
              <button
                key={f}
                className={`filter-pill ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? 'All' : f === 'active' ? '🟢 Active' : f === 'expiring' ? '🟠 Expiring' : '🔴 Expired'}
              </button>
            ))}
          </div>
        </div>

        {!loading && !error && filtered.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', padding: '0 28px', marginBottom: 16 }}>
            <div
              className="vc-checkbox-wrapper"
              onClick={() => {
                if (allSelected) setSelectedVehicleIds(new Set());
                else setSelectedVehicleIds(new Set(validSelectableIds));
              }}
            >
              <input type="checkbox" className="vc-checkbox" checked={allSelected} readOnly />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--pw-text-primary)', marginLeft: 8 }}>Select All</div>
          </div>
        )}
      </div>

      <div className="vehicle-list">
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spinner size="lg" color="var(--pw-color-primary)" />
            <p style={{ color: 'var(--pw-text-secondary)', marginTop: 16, fontSize: 14 }}>Loading vehicles...</p>
          </div>
        )}

        {error && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--pw-text-secondary)' }}>
            <p>{error}</p>
            <button
              className="btn btn-primary"
              style={{ marginTop: 16, padding: '10px 24px', fontSize: 14 }}
              onClick={fetchData}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--pw-text-secondary)' }}>
            No vehicles match your filters.
          </div>
        )}

        {!loading && !error && filtered.map((v, idx) => {
          const subDays = getDaysLeft(v.subscriptionExpiry);
          const warDays = getDaysLeft(v.warrantyExpiry);
          const subPill = getStatusPill(subDays);
          const warPill = getStatusPill(warDays, { showBlocked: false });
          const isSelected = selectedVehicleIds.has(v.id);

          return (
            <div
              key={v.id}
              className="vehicle-card"
              style={{ animationDelay: `${idx * 0.02}s`, gridTemplateColumns: 'auto 1fr auto auto' }}
              onClick={e => {
                if (subDays <= SUBSCRIPTION_SIM_BLOCK_THRESHOLD_DAYS_LEFT) {
                  navigate(`${PATHS.PLANS_AND_WARRANTY}/${v.id}`, { state: { apiData } });
                  return;
                }
                if (selectedVehicleIds.size > 0) {
                  toggleSelect(v.id, e);
                } else {
                  navigate(`${PATHS.PLANS_AND_WARRANTY}/${v.id}`, { state: { apiData } });
                }
              }}
            >
              {subDays <= SUBSCRIPTION_SIM_BLOCK_THRESHOLD_DAYS_LEFT ? (
                <div style={{ width: 14, margin: '0 -10px 0 0' }} />
              ) : (
                <div className="vc-checkbox-wrapper" onClick={e => toggleSelect(v.id, e)}>
                  <input type="checkbox" className="vc-checkbox" checked={isSelected} readOnly />
                </div>
              )}

              <div className="vc-info">
                <h3>{v.displayName}</h3>
                <span>{v.regNumber}</span>
              </div>

              <div className="vc-statuses">
                <div className="vc-status-block">
                  <small>Subscription</small>
                  <span className={`status-pill pill-${subPill.color}`}>{subPill.text}</span>
                </div>
                <div className="vc-status-block">
                  <small>Warranty</small>
                  <span className={`status-pill pill-${warPill.color}`}>{warPill.text}</span>
                </div>
              </div>

              <svg width="20" height="20" className="vc-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlansAndWarranty;
